// EmailThreadAttachments - reads an email_thread upload's attachment manifest
// and exposes two surfaces: ``EmailThreadTypeCell`` (the "N messages ·
// M attachments" / "N messages" summary that replaces the static
// "Email thread" label in the Type column), and ``EmailThreadAttachmentsRows``
// (the nested child rows rendered beneath the parent when the chevron is
// expanded).
//
// Both surfaces consume the same /uploads/extracted query React Query is
// already firing for ExtractionStatusButton + ExtractionIssueMarker on every
// email_thread row, so adding these doesn't add a round trip.
//
// The message COUNT is not part of the manifest: a thread's structural metadata is
// provenance, so it lives on the thread's gmail_thread sighting (see
// SightingList.threadMessageCount) and is passed in as a prop.
//
// Each INGESTED attachment is a first-class ``DBUpload`` (deduped by content
// identity - see gmail_sync._make_attachment_ingestor), and its ``upload_id`` rides
// along in the manifest entry. That lets us render each attachment's OWN extracted
// text via the same self-contained ExtractedTextSection the top-level files use,
// keyed on the attachment's upload_id. Skipped / not-pulled attachments have no
// upload_id (nothing to read) and stay metadata-only.
import { useSightingsFor, useUploadExtracted } from "../api";
import type { ThreadAttachmentEntry } from "../api/hooks/useUploadExtracted";
import { dedupeRenderedSightings, SightingList } from "./SightingList";
import { CollapsibleSection } from "./CollapsibleSection";
import { ExtractedTextSection } from "./ExtractedTextSection";
import { isTextSelectionClick, SELECTABLE_LABEL_STYLE } from "./selectableButton";
import { RegisterBadge } from "./RegisterBadge";
import { PossibleOtherSiteBadge } from "./SiteWarningBadge";
import { Chevron } from "./Chevron";

type AttachmentEntry = ThreadAttachmentEntry;

const formatBytes = (n: number | null): string => {
  if (n === null || n === undefined) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const useEmailThreadManifest = (
  uploadId: string | null,
): {
  attachmentCount: number;
  attachments: AttachmentEntry[];
} => {
  const { data } = useUploadExtracted(uploadId);
  // Absent while the view is loading, so the row degrades to no chevron, no crash.
  const attachments = data?.thread_attachments ?? [];
  return { attachmentCount: attachments.length, attachments };
};

interface TypeCellProps {
  uploadId: string;
  // From the upload's gmail_thread sighting (``threadMessageCount``), or the
  // gmail-sync row's own count on the listener tree. Null while unknown.
  messageCount: number | null;
}

// Replaces the static "Email thread" label in the Type column with a more
// useful summary: "3 messages · 5 attachments" when there are attachments,
// "3 messages" otherwise. Falls back to "Email thread" while the count is
// unknown so the column doesn't flash empty.
//
// Informational only - the CONTROL that reveals the attachments is
// ``EmailThreadAttachmentsToggle``, rendered beside "Show data" where an
// operator looks for per-file actions.
export const EmailThreadTypeCell = ({ uploadId, messageCount }: TypeCellProps): JSX.Element => {
  const { attachmentCount } = useEmailThreadManifest(uploadId);
  if (messageCount === null) {
    // The count comes from the thread's sighting, which a thread ingested before
    // the shape columns existed carries only once the backfill (or its next
    // extraction) has run. Don't drop the attachment count too - it is the half
    // that is still known, and it still tells the operator the row has content.
    if (attachmentCount === 0) {
      return <span style={{ color: "#9ca3af" }}>Email thread</span>;
    }
    return <span>{`${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`}</span>;
  }
  const messagesPart = `${messageCount} message${messageCount === 1 ? "" : "s"}`;
  if (attachmentCount === 0) {
    return <span>{messagesPart}</span>;
  }
  const attachmentsPart = `${attachmentCount} attachment${attachmentCount === 1 ? "" : "s"}`;
  return (
    <span>
      {messagesPart} <span style={{ color: "#9ca3af" }}>·</span> {attachmentsPart}
    </span>
  );
};

// The labelled expander: "3 attachments" and a chevron, sitting beside the "Show data"
// button so the control for a thread's attachments is where every other per-file
// action lives. Replaces a 14px chevron at the far left of the row, which was too
// subtle to find - operators read "3 attachments" in the Type column and had no
// idea how to see them. Styled to match ExtractionStatusButton / the sighting
// summary rather than inventing a third look.
export const EmailThreadAttachmentsToggle = ({
  uploadId,
  expanded,
  onToggle,
}: ChevronProps): JSX.Element | null => {
  const { attachmentCount } = useEmailThreadManifest(uploadId);
  if (attachmentCount === 0) return null;
  return (
    <button
      type="button"
      onClick={(e) => {
        if (isTextSelectionClick(e)) return;
        onToggle();
      }}
      aria-expanded={expanded}
      title={
        expanded
          ? "Hide these attachments"
          : "Show each attachment, where it came from, and its extracted text"
      }
      style={{
        marginLeft: 8,
        background: "none",
        border: "none",
        padding: 0,
        fontSize: 11,
        color: "#2563eb",
        textDecoration: "underline",
        cursor: "pointer",
        // No `nowrap`: on a narrow screen the row wraps this control onto its
        // own line rather than pushing the row past the viewport.
        maxWidth: "100%",
        textAlign: "left",
        ...SELECTABLE_LABEL_STYLE,
      }}
    >
      {attachmentCount} attachment{attachmentCount === 1 ? "" : "s"} <Chevron open={expanded} />
    </button>
  );
};

interface ChevronProps {
  uploadId: string;
  expanded: boolean;
  onToggle: () => void;
}

// Small inline chevron that renders only when the thread has at least one
// attachment. Sits to the left of the filename (and to the left of any
// ExtractionIssueMarker / OutOfDateIcon), per Noaa's "expander to the left of
// it" placement. A thread with zero attachments gets a 14px spacer so the
// filename column doesn't shift left vs. non-thread rows.
export const EmailThreadAttachmentsChevron = ({
  uploadId,
  expanded,
  onToggle,
}: ChevronProps): JSX.Element => {
  const { attachmentCount } = useEmailThreadManifest(uploadId);
  if (attachmentCount === 0) {
    return <span style={{ display: "inline-block", width: 14, marginRight: 4 }} />;
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={
        expanded ? "Hide thread attachments" : "Show thread attachments"
      }
      title={
        expanded ? "Hide thread attachments" : "Show thread attachments"
      }
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 14,
        height: 14,
        marginRight: 4,
        padding: 0,
        background: "transparent",
        border: "none",
        cursor: "pointer",
        color: "#6b7280",
        fontSize: 11,
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      <Chevron tree open={expanded} />
    </button>
  );
};

interface RowsProps {
  /** The thread whose manifest lists its attachments; null for a withheld one, whose
   * attachments come only from ``nestedAttachments``. */
  uploadId: string | null;
  /** The project page it renders on, for the register link. */
  projectId: string;
  // Total column count on the parent table so the nested ``<td>`` spans
  // the full row width without breaking the column alignment of the
  // surrounding sibling rows.
  colSpan: number;
  // Attachment rows the BE resolved onto this thread by PROVENANCE, whatever the
  // manifest says. The manifest is rebuilt on every re-pull and empty in between, so a
  // file it has not caught up with would be nested nowhere and - being nested - listed
  // nowhere either. Rendering the union is what makes that impossible.
  nestedAttachments?: { upload_id: string; original_name: string }[];
}

// One attachment's "seen at" places, from the shared list poll. Renders nothing
// for a not-pulled attachment (no upload_id) or a single-place one - the row is
// already inside its thread, so one sighting would just restate that.
const AttachmentSightings = ({
  uploadId,
}: {
  uploadId?: string | null;
}): JSX.Element | null => {
  const sightings = dedupeRenderedSightings(useSightingsFor(uploadId));
  if (sightings.length < 2) return null;
  return (
    // whiteSpace: normal RESETS the nowrap the filename cell sets (it keeps the
    // filename itself on one line). Inherited, it made these long provenance
    // sentences unwrappable, so a Gmail attachment with two thread sightings blew
    // the table out past the page width.
    <span style={{ display: "block", marginTop: 2, whiteSpace: "normal" }}>
      <SightingList sightings={sightings} />
    </span>
  );
};

// Render the attachment manifest as a tightly-spaced nested table, indented
// + smaller font so it visually reads as "child of the row above" (Noaa: "the
// size of the font on the attachments seems too large to be a child of an
// email thread").
export const EmailThreadAttachmentsRows = ({
  uploadId,
  projectId,
  colSpan,
  nestedAttachments = [],
}: RowsProps): JSX.Element | null => {
  const { attachments: manifest } = useEmailThreadManifest(uploadId);
  // Union, manifest first: it carries the richer detail (size, type, skip reason) and
  // the entries that were never ingested at all. Anything the BE filed under this thread
  // that the manifest does not name is appended from its upload row, so a manifest that
  // is stale, mid-rebuild or empty can never hide a file.
  const named = new Set(
    manifest.map((a) => a.upload_id).filter((id): id is string => !!id),
  );
  const attachments: AttachmentEntry[] = [
    ...manifest,
    ...nestedAttachments
      .filter((a) => !named.has(a.upload_id))
      .map((a) => ({
        filename: a.original_name,
        mime_type: null,
        size_bytes: null,
        status: "ingested",
        note: null,
        content_summary: null,
        upload_id: a.upload_id,
      })),
  ];
  if (attachments.length === 0) return null;
  return (
    <tr>
      <td
        colSpan={colSpan}
        style={{
          padding: "6px 0 6px 56px",
          background: "#fafafa",
          borderBottom: "1px solid #f3f4f6",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: 11,
            color: "#4b5563",
          }}
        >
          <tbody>
            {attachments.flatMap((att, idx) => {
              const isSkipped = att.status === "skipped";
              const rows: JSX.Element[] = [
                <tr key={`${att.filename}-${idx}`}>
                  <td
                    style={{
                      padding: "2px 8px 2px 0",
                      whiteSpace: "nowrap",
                      color: isSkipped ? "#9ca3af" : "#374151",
                    }}
                  >
                    <span style={{ marginRight: 6 }}>📎</span>
                    {att.filename}
                    <PossibleOtherSiteBadge uploadId={att.upload_id} />
                    {att.upload_id && (
                      <RegisterBadge documentId={att.upload_id} projectId={projectId} />
                    )}
                    {/* The same attachment routinely arrives on several messages
                        or threads; dedup keeps ONE upload and records each arrival
                        as a sighting. These rows are fed by the thread's facts
                        manifest, which has no sightings, so they come from the
                        shared /uploads/list poll via SightingsByUploadProvider.
                        Collapsed past one place, like every other surface. */}
                    <AttachmentSightings uploadId={att.upload_id} />
                  </td>
                  <td
                    style={{
                      padding: "2px 8px",
                      whiteSpace: "nowrap",
                      color: "#9ca3af",
                      width: 80,
                    }}
                  >
                    {formatBytes(att.size_bytes)}
                  </td>
                  <td style={{ padding: "2px 8px", color: "#6b7280" }}>
                    {isSkipped ? (
                      `Not pulled${att.note ? ` - ${att.note}` : ""}`
                    ) : (
                      // An ingested attachment is a first-class upload: its real
                      // extracted text renders in the card below, so "no summary"
                      // would be misleading. content_summary only appears on
                      // legacy manifests extracted before attachments were
                      // promoted to uploads.
                      att.content_summary || (
                        <span style={{ color: "#9ca3af" }}>
                          {att.upload_id
                            ? "Saved as a file - full text below"
                            : "No summary available"}
                        </span>
                      )
                    )}
                  </td>
                </tr>,
              ];
              // Ingested attachment -> let the operator open its own extracted
              // text (same self-contained lazy-fetch card as the top-level files,
              // keyed on the attachment's upload_id).
              if (!isSkipped && att.upload_id) {
                rows.push(
                  <tr key={`${att.filename}-${idx}-text`}>
                    <td colSpan={3} style={{ padding: 0 }}>
                      {/* Collapsed, and mounted only when opened: the section
                          fetches on mount, and a thread can carry many
                          attachments - eager mounting would fire one text fetch
                          per attachment on every thread expand. */}
                      <CollapsibleSection title="Text" defaultExpanded={false}>
                        <ExtractedTextSection
                          uploadId={att.upload_id}
                          hasExtractedText
                        />
                      </CollapsibleSection>
                    </td>
                  </tr>,
                );
              }
              return rows;
            })}
          </tbody>
        </table>
      </td>
    </tr>
  );
};
